'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'

// The theme, as a preference: light by default (the design system is light
// first), the OS preference on a first visit, and the user's own choice once
// they toggle it -- persisted, so it survives reloads, which it did not
// before 2026-10-21 (the toggle only lasted a session and the OS always won).
//
// The source of truth for colour is CSS: `:root` holds the light tokens and
// `html[data-theme="dark"]` the dark ones (app/globals.css). app/layout.tsx
// runs a pre-paint script that sets data-theme from the same rule, so the
// first paint is already right; this hook then reads that attribute, keeps
// it in step with React state for the components that still branch on the
// `theme` prop, and resolves the tokens for the chart code that needs
// literal colours (useThemeTokens).

export const THEME_STORAGE_KEY = 'tf-theme'

const isTheme = (v: unknown): v is ProcessedTheme => v === 'dark' || v === 'light'

function readStoredTheme(): ProcessedTheme | null {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(v) ? v : null
  } catch {
    return null
  }
}

function systemTheme(): ProcessedTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(theme: ProcessedTheme) {
  document.documentElement.dataset.theme = theme
  // Native UI (select lists, scrollbars, date pickers) reads color-scheme.
  document.documentElement.style.colorScheme = theme
}

export function useThemePreference() {
  // SSR renders light; the pre-paint script has already set the attribute
  // by the time this mounts, so the first effect only aligns React state.
  const [theme, setThemeState] = useState<ProcessedTheme>('light')

  useEffect(() => {
    const fromDom = document.documentElement.dataset.theme
    const initial = isTheme(fromDom) ? fromDom : (readStoredTheme() ?? systemTheme())
    setThemeState(initial)
    applyTheme(initial)
  }, [])

  // Follow the OS only while the user has not chosen.
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mql) return
    const onChange = (e: MediaQueryListEvent) => {
      if (readStoredTheme()) return
      const next: ProcessedTheme = e.matches ? 'dark' : 'light'
      setThemeState(next)
      applyTheme(next)
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const setTheme = useCallback((next: ProcessedTheme) => {
    try { window.localStorage.setItem(THEME_STORAGE_KEY, next) } catch { /* private mode: the choice lasts the session */ }
    setThemeState(next)
    applyTheme(next)
  }, [])

  return { theme, setTheme, isDark: theme === 'dark' }
}

// The tokens a chart or map needs as literal colours (recharts/d3 attributes
// take any CSS colour string, oklch included). Resolved from the computed
// style once per theme; the light values are the fallback before mount.
export const TOKEN_NAMES = [
  'background', 'card', 'border', 'panel-rule', 'foreground', 'primary', 'muted', 'muted-foreground', 'subtle-text',
  'signal', 'signal-surface', 'success', 'success-surface', 'warning', 'warning-surface', 'destructive', 'danger-surface',
  'brand-ctt', 'brand-mbway', 'brand-viaverde', 'brand-bcp', 'brand-sixt',
] as const
export type TokenName = (typeof TOKEN_NAMES)[number]
export type ThemeTokens = Record<TokenName, string>

const LIGHT_FALLBACK: ThemeTokens = {
  'background': 'oklch(96.5% 0.005 95)', 'card': 'oklch(98.5% 0.004 95)', 'border': 'oklch(88.5% 0.006 95)',
  'panel-rule': 'oklch(76% 0.019 250)', 'foreground': 'oklch(22% 0.012 265)', 'primary': 'oklch(22% 0.012 265)',
  'muted': 'oklch(92.5% 0.005 95)', 'muted-foreground': 'oklch(47% 0.012 265)', 'subtle-text': 'oklch(55% 0.012 265)',
  'signal': 'oklch(52% 0.19 255)', 'signal-surface': 'oklch(95% 0.02 205)', 'success': 'oklch(46% 0.11 160)',
  'success-surface': 'oklch(95% 0.022 160)', 'warning': 'oklch(52% 0.115 75)', 'warning-surface': 'oklch(97.5% 0.025 85)',
  'destructive': 'oklch(55% 0.19 25)', 'danger-surface': 'oklch(97.8% 0.009 25)',
  'brand-ctt': 'oklch(56% 0.21 26)', 'brand-mbway': 'oklch(49% 0.075 245)', 'brand-viaverde': 'oklch(54% 0.136 151)',
  'brand-bcp': 'oklch(52% 0.17 355)', 'brand-sixt': 'oklch(58% 0.15 55)',
}

export function readThemeTokens(): ThemeTokens {
  if (typeof window === 'undefined') return LIGHT_FALLBACK
  const style = getComputedStyle(document.documentElement)
  const out = { ...LIGHT_FALLBACK }
  for (const name of TOKEN_NAMES) {
    const v = style.getPropertyValue(`--${name}`).trim()
    if (v) out[name] = v
  }
  return out
}

export function useThemeTokens(theme: ProcessedTheme): ThemeTokens {
  const [tokens, setTokens] = useState<ThemeTokens>(LIGHT_FALLBACK)
  useEffect(() => { setTokens(readThemeTokens()) }, [theme])
  return useMemo(() => tokens, [tokens])
}

// Categorical series slots for charts (plan C4): fixed order so a source or
// brand keeps its colour across every chart.
export function seriesColors(t: ThemeTokens): string[] {
  return [t.signal, t.success, t.warning, t.destructive, t['brand-mbway'], t['brand-bcp'], t['brand-sixt'], t['panel-rule']]
}
